import Analytics from '@opencare/analytics';
import moment from 'moment';

import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Div, VisuallyHidden } from 'common/styles/oc-styled-components';
import { colors } from 'common/styles/style-constants';
import { resetMatches } from 'connect/actions';
import { makeSelectDataResults, selectDataMatch } from 'connect/selectors';
import { Icon } from 'opencare-crown';

import { ExistingMatchSearchOptions } from 'pages/funnel/components/ExistingMatch/ExistingMatchSearchOptions';
import { useUpdateLocationInStorage } from 'pages/funnel/storage/hooks';
import { useFunnelStorage } from 'pages/funnel/storage/Storage';
import { STEP_KEY } from 'pages/funnel/utils';
import {
  DIMENSIONS,
  useWindowDimensions,
} from 'providers/WindowDimensionsProvider';
import { useAnalyticsService } from 'services/AnalyticsService';

import {
  OptionBlock,
  OptionDescription,
  OptionTitle,
  Title,
} from '../../../common/styled-components';
import {
  ExistingButton,
  ExistingIconWrapper,
  ExistingWrapper,
  StepWrapper,
} from '../../styled-components';

import { ProfilePhoto, ProfilePhotoWrapper } from './styled-components';

const analytics = new Analytics();

const ExistingMatch = ({ goToMatch, clearFunnel }) => {
  const dispatch = useDispatch();
  const storage = useFunnelStorage();
  const { layout } = useWindowDimensions();

  const analyticsService = useAnalyticsService();

  const existingMatch = useSelector(selectDataMatch);
  const searchResults = useSelector(makeSelectDataResults);

  const prefillData = storage.getPrefillData();
  const urlSearchLocation = (() => {
    const prefilledLocation = prefillData?.[STEP_KEY.LOCATION];
    if (
      prefilledLocation?.formattedAddress
      === existingMatch?.location?.formattedAddress
    ) {
      return null;
    }

    return prefilledLocation;
  })();

  const [shouldUpdateLocation, setUpdateLocation] = useState(false);
  const locationUpdateState = useUpdateLocationInStorage({
    newLocation: urlSearchLocation,
    shouldUpdate: shouldUpdateLocation,
  });

  const searchLocality = existingMatch?.location?.address?.locality;
  const searchDateString = moment(existingMatch?.createdAt).format('MMMM Do');
  const providerPhotos = searchResults
    .map(
      (result) => (Array.isArray(result?.providers) ? result.providers : [])[0],
    )
    .filter(Boolean)
    .map((provider) => {
      const photo = provider.doctor?.primaryPhoto;

      if (!photo?.name) {
        return null;
      }

      return {
        id: photo.name,
        url: `https://images.opencare.com/${photo.name}-200x200.${
          photo.extension
        }`,
        doctor: provider.doctor.name,
      };
    })
    .filter(Boolean)
    .slice(0, 3);

  useEffect(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_existing-match',
      ...analyticsService.analyticsData,
    });
  }, []);

  useEffect(() => {
    if (!locationUpdateState.isFinished) {
      return;
    }

    dispatch(resetMatches());
    goToMatch();
  }, [locationUpdateState]);

  const returnToMatch = () => {
    analytics.trackClick({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_existing-match',
      name: 'wizard',
      section: 'existing-match',
      content: {
        response: 'return-to-match',
      },
      position: [1],
    });

    goToMatch();
  };

  const startNewSearch = () => {
    analytics.trackClick({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_existing-match',
      name: 'wizard',
      section: 'existing-match',
      content: {
        response: 'start-a-new-search',
      },
      position: [2],
    });

    clearFunnel();
  };

  const updateMatch = () => {
    analytics.trackClick({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_existing-match',
      name: 'wizard',
      section: 'existing-match',
      content: {
        response: 'refresh-the-search',
      },
      position: [3],
    });

    setUpdateLocation(true);
  };

  const commonStyles = {
    titleIcon: {
      fontSize: '38px',
      color: colors.sage6,
    },
  };

  const StartNewSearch = (
    <Div>
      <ExistingButton
        data-testid="restart"
        type="button"
        onClick={startNewSearch}
      >
        <ExistingIconWrapper aria-hidden>
          <Div position="relative">
            <Icon type="plus-circle" style={commonStyles.titleIcon} />
            <Icon
              type="search"
              style={{
                ...commonStyles.titleIcon,
                position: 'absolute',
                bottom: '-5px',
                right: '-5px',
                fontSize: '20px',
                borderRadius: '15px',
                backgroundColor: colors.white,
              }}
            />
          </Div>
        </ExistingIconWrapper>
        <OptionTitle aria-hidden>Start a new search</OptionTitle>
        <OptionDescription aria-hidden>
          Answer a new set of questions and get matched with the best dentists
          near you
        </OptionDescription>
        <VisuallyHidden>
          Start a new search. Answer a new set of questions and get matched with
          the best dentists near you
        </VisuallyHidden>
      </ExistingButton>
    </Div>
  );
  return (
    <ExistingWrapper data-testid="existing-match">
      {urlSearchLocation?.formattedAddress
      && <Title>Searching for: {urlSearchLocation?.formattedAddress}</Title>}
      <StepWrapper>
        {existingMatch?.createdAt
        && (
          <OptionBlock row={urlSearchLocation?.formattedAddress ? 2 : 1}>
            <Div>
              <ExistingButton
                data-testid="continue"
                type="button"
                onClick={returnToMatch}
              >
                <ExistingIconWrapper
                  aria-hidden
                  as="ul"
                  width="120px"
                  textAlign="left"
                >
                  {providerPhotos.map((photo, i) => (
                    <ProfilePhotoWrapper key={photo.id} position={i}>
                      <ProfilePhoto
                        data-testid={`photo-${photo.id}`}
                        src={photo.url}
                        alt={photo.doctor}
                      />
                    </ProfilePhotoWrapper>
                  ))}
                </ExistingIconWrapper>
                <OptionTitle aria-hidden>
                  Return to previous results
                </OptionTitle>
                <OptionDescription aria-hidden>
                  View the dentists we recommended in {searchLocality} on{' '}
                  {searchDateString}
                </OptionDescription>
                <VisuallyHidden>
                  Return to previous results. View the dentists we recommended
                  in {searchLocality} on {searchDateString} including{' '}
                  {providerPhotos.map((provider) => provider.doctor).join(', ')}.
                </VisuallyHidden>
                <ExistingMatchSearchOptions
                  match={existingMatch}
                  locationLabel="Previous Location"
                />
              </ExistingButton>
            </Div>

            {layout === DIMENSIONS.MOBILE && StartNewSearch}

            {urlSearchLocation?.formattedAddress
            && (
              <Div>
                <ExistingButton
                  data-testid="refresh"
                  type="button"
                  onClick={updateMatch}
                >
                  <ExistingIconWrapper aria-hidden>
                    <Icon type="search" style={commonStyles.titleIcon} />
                  </ExistingIconWrapper>
                  <OptionTitle aria-hidden>Update results</OptionTitle>
                  <OptionDescription aria-hidden>
                    We'll combine your previous funnel responses with your new
                    location to generate a new list of recommended practices
                    near you.
                  </OptionDescription>
                  <VisuallyHidden>
                    Update results. We'll combine your previous funnel responses
                    with your new location to generate a new list of recommended
                    practices near you.
                  </VisuallyHidden>
                  <ExistingMatchSearchOptions
                    match={existingMatch}
                    location={urlSearchLocation}
                    locationLabel="Current Location"
                  />
                </ExistingButton>
              </Div>
            )}
          </OptionBlock>
        )}

        {layout !== DIMENSIONS.MOBILE
        && <OptionBlock row={1}>{StartNewSearch}</OptionBlock>}
      </StepWrapper>
    </ExistingWrapper>
  );
};

ExistingMatch.propTypes = {
  goToMatch: PropTypes.func.isRequired,
  clearFunnel: PropTypes.func.isRequired,
};

export default ExistingMatch;
